import { Children, isValidElement, useMemo } from "react";
import type { ReactNode } from "react";

import { useStableRest } from "../../utils/use-stable-rest";
import type {
  ListToolbarClassNames,
  ListToolbarRootAttributes,
  ListToolbarViewModel,
  UseListToolbarOptions,
} from "./types-list-toolbar";

const NO_CLASS_NAMES: ListToolbarClassNames = {};

/** `displayName` das partes que vão para o fim da barra (grupo `end` do `Toolbar`); as demais ficam no `start`. */
const END_PARTS = ["ListToolbar.Actions", "ListToolbar.Menu"];

/** Separa os filhos do Root: `Actions` e `Menu` vão para o `end` do `Toolbar`, o resto para o `start`, na ordem recebida. */
function partitionToolbarChildren(children: ReactNode) {
  const start: ReactNode[] = [];
  const end: ReactNode[] = [];
  for (const child of Children.toArray(children)) {
    const name = isValidElement(child) ? (child.type as { displayName?: string }).displayName : "";
    (name && END_PARTS.includes(name) ? end : start).push(child);
  }
  return { start, end };
}

/** ViewModel do ListToolbar.Root: flags da moldura, filhos por grupo, classes por parte e testids derivados; sem JSX. */
export function useListToolbar(
  {
    testId,
    framed = true,
    "aria-label": ariaLabel,
    className,
    style,
    classNames,
    ...props
  }: UseListToolbarOptions & ListToolbarRootAttributes = {},
  children?: ReactNode,
): ListToolbarViewModel {
  const rest = useStableRest(props);
  const context = useMemo(
    () => ({
      classNames: classNames ?? NO_CLASS_NAMES,
      partTestId: (part: string) => (testId ? `${testId}-${part}` : undefined),
    }),
    [classNames, testId],
  );

  return {
    context,
    testId,
    framed,
    ariaLabel,
    className,
    style,
    rest,
    ...partitionToolbarChildren(children),
  };
}
